import { useState, type FormEvent } from 'react';

export interface GrantDraft {
    repoId: string;
    scope: { type: 'all_content' } | { type: 'md_only' } | { type: 'folders'; folders: string[] };
}

interface Props {
    initial?: Partial<GrantDraft>;
    onSubmit: (draft: GrantDraft) => void;
    submitLabel?: string;
    disabled?: boolean;
}

type ScopeType = 'all_content' | 'md_only' | 'folders';

export function GrantEditor({ initial, onSubmit, submitLabel = 'Save', disabled }: Props) {
    const [repoId, setRepoId] = useState(initial?.repoId ?? '');
    const [scopeType, setScopeType] = useState<ScopeType>(initial?.scope?.type ?? 'all_content');
    const [folders, setFolders] = useState<string>(
        initial?.scope && initial.scope.type === 'folders' ? initial.scope.folders.join('\n') : ''
    );
    const [error, setError] = useState<string | null>(null);

    function handle(e: FormEvent) {
        e.preventDefault();
        if (!repoId.match(/^[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+$/)) {
            setError('repoId must be in form owner/repo');
            return;
        }
        let scope: GrantDraft['scope'];
        if (scopeType === 'folders') {
            const list = folders.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
            if (list.length === 0) {
                setError('Folders scope needs at least one folder');
                return;
            }
            scope = { type: 'folders', folders: list };
        } else {
            scope = { type: scopeType };
        }
        setError(null);
        onSubmit({ repoId, scope });
        if (!initial) {
            // reset for "add" usage
            setRepoId('');
            setScopeType('all_content');
            setFolders('');
        }
    }

    return (
        <form className="grant-editor" onSubmit={handle}>
            <div className="grant-row">
                <input
                    type="text"
                    value={repoId}
                    onChange={(e) => setRepoId(e.target.value)}
                    placeholder="owner/repo"
                    disabled={disabled}
                />
                <select
                    value={scopeType}
                    onChange={(e) => setScopeType(e.target.value as ScopeType)}
                    disabled={disabled}
                >
                    <option value="all_content">All content</option>
                    <option value="md_only">Markdown only</option>
                    <option value="folders">Specific folders</option>
                </select>
                {scopeType === 'folders' && (
                    <textarea
                        value={folders}
                        onChange={(e) => setFolders(e.target.value)}
                        placeholder={'docs/\nrunbooks/auth/'}
                        rows={2}
                        disabled={disabled}
                    />
                )}
                <button type="submit" className="btn btn--primary btn--small" disabled={disabled}>
                    {submitLabel}
                </button>
            </div>
            {error && <div className="form-error">{error}</div>}
        </form>
    );
}
